/**
 * A release's cover art, wherever it appears.
 *
 * THE single renderer for release artwork, for the same reason SongArtwork is the single
 * renderer for a song's: the dashboard list, the wizard's artwork step, the review step
 * and the detail page all draw it, and four answers to "what is this release's cover?"
 * is how the Studio and the Library ended up disagreeing before SongArtwork existed.
 *
 * The resolution order, which is §11's requirement expressed as code:
 *   1. The release's OWN uploaded image, when there is one.
 *   2. Otherwise the artwork of the song the user explicitly chose to reuse.
 *   3. Otherwise the gradient, seeded on the release id.
 *
 * Note what is NOT here: writing to `Song.coverUrl`. Choosing release artwork never
 * touches the song's own — §11 says the original song artwork must not be silently
 * overwritten, so the release carries its own field and the song is only ever read.
 *
 * SIMULATED on the gradient path, exactly as elsewhere: a gradient is not an image file,
 * which is why the submission plan marks the artwork upload step unresolved when that is
 * all a release has.
 */
import type { CSSProperties, ReactNode } from 'react';
import { ArtworkPlaceholder, SongArtwork } from '../songs';
import type { PublisherRelease, Song } from '../../types/domain';

export interface ReleaseArtworkProps {
  release: PublisherRelease;
  /** The library, so a reused song cover can be resolved. */
  songs: Song[];
  /** Height in px. */
  height?: number;
  radius?: number;
  children?: ReactNode;
  style?: CSSProperties;
}

export default function ReleaseArtwork({
  release,
  songs,
  height,
  radius,
  children,
  style,
}: ReleaseArtworkProps) {
  const box: CSSProperties = { height, borderRadius: radius, ...style };

  // 1 — the release's own image.
  if (release.artworkUrl) {
    return (
      <div style={{ position: 'relative', overflow: 'hidden', ...box }}>
        <img
          src={release.artworkUrl}
          alt={`Cover art for ${release.title || 'this release'}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {children}
      </div>
    );
  }

  /*
    2 — the song's artwork, but only when the user CHOSE it.

    Keyed on `artworkFromSongId` rather than falling back to the first song on the
    release: reusing a song's cover is a decision §11 requires the user to make, and
    borrowing it automatically would show artwork on the review screen that nobody
    picked and that would then be delivered.
  */
  if (release.artworkFromSongId) {
    const song = songs.find((s) => s.id === release.artworkFromSongId);
    if (song) {
      return (
        <SongArtwork song={song} height={height} radius={radius} style={style}>
          {children}
        </SongArtwork>
      );
    }
  }

  // 3 — the gradient. Seeded on the release id, so a release keeps one colour.
  return (
    <ArtworkPlaceholder id={release.id} height={height} depth="card" style={box}>
      {children}
    </ArtworkPlaceholder>
  );
}
